"use client"

import { useState, useEffect } from "react"
import { Link, useLocation } from "react-router-dom"
import {
  ChatAlt2Icon,
  PhotographIcon,
  NewspaperIcon,
  BriefcaseIcon,
  MenuIcon,
  CakeIcon,
  LogoutIcon,
} from "@heroicons/react/outline"

const Sidebar = () => {
  const location = useLocation()
  const [isMobile, setIsMobile] = useState(false)
  const [isOpen, setIsOpen] = useState(false)

  // Check if mobile on mount and when window resizes
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }

    // Initial check
    checkIfMobile()

    // Add event listener
    window.addEventListener("resize", checkIfMobile)

    // Clean up
    return () => {
      window.removeEventListener("resize", checkIfMobile)
    }
  }, [])

  // Define only the 4 main navigation items with proper paths
  const menuItems = [
    { name: "Contact", icon: ChatAlt2Icon, path: "/contact" },
    { name: "Gallery", icon: PhotographIcon, path: "/gallery" },
    { name: "Services", icon: BriefcaseIcon, path: "/services" },
    { name: "Blog", icon: NewspaperIcon, path: "/blog" },
    { name: "caregiver", icon: CakeIcon, path: "/training" },
  ]

  // Helper function to check if a menu item is active
  const isMenuItemActive = (path) => {
    // For pages, check if the pathname starts with the path (to handle sub-routes)
    return location.pathname.startsWith(path)
  }

  const SidebarContent = () => (
    <div className="flex flex-col h-full py-2">
      {/* Logo Section - Adjusted padding to move it up */}
      <div className="px-2 pt-1 pb-2 border-b border-gray-200">
        <h1 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-blue-800">
          Admin Portal
        </h1>
      </div>

      {/* Navigation Menu - Only 4 functions */}
      <nav className="flex-1 px-4 py-6 space-y-3 overflow-y-auto">
        {menuItems.map((item) => {
          const Icon = item.icon
          const isActive = isMenuItemActive(item.path)

          return (
            <Link
              key={item.name}
              to={item.path}
              className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 group
                ${isActive ? "bg-blue-50 text-blue-700" : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"}`}
              onClick={() => isMobile && setIsOpen(false)}
            >
              <Icon
                className={`w-5 h-5 mr-3 transition-colors duration-200
                ${isActive ? "text-blue-700" : "text-gray-400 group-hover:text-gray-900"}`}
              />
              {item.name}
            </Link>
          )
        })}
      </nav>

      {/* Footer Section */}
      <div className="p-4 border-t border-gray-200">
        <button className="flex items-center w-full px-4 py-3 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200">
          <LogoutIcon className="w-5 h-5 mr-3" />
          Logout
        </button>
      </div>

      {/* User Profile Section */}
      <div className="p-4 border-t border-gray-200">
        <div className="flex items-center space-x-3">
          <div>
          </div>
        </div>
      </div>
    </div>
  )

  // Mobile sidebar with overlay
  if (isMobile) {
    return (
      <>
        {/* Mobile Header - Adjusted padding to move it up */}
        <div className="fixed top-0 left-0 right-0 z-20 flex items-center justify-between p-4 bg-white border-b border-gray-200">
          <h1 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-blue-800">
            Admin Portal
          </h1>
          <button onClick={() => setIsOpen(!isOpen)} className="p-2 text-gray-600 rounded-md hover:bg-gray-100">
            <MenuIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Mobile Sidebar */}
        <div className={`fixed inset-0 z-30 transition-all duration-300 ${isOpen ? "visible" : "invisible"}`}>
          {/* Backdrop */}
          <div
            className={`absolute inset-0 bg-black transition-opacity duration-300 ${
              isOpen ? "opacity-50" : "opacity-0"
            }`}
            onClick={() => setIsOpen(false)}
          ></div>

          {/* Sidebar */}
          <div
            className={`absolute top-0 left-0 bottom-0 w-64 bg-white shadow-xl transition-transform duration-300 ${
              isOpen ? "translate-x-0" : "-translate-x-full"
            }`}
          >
            <SidebarContent />
          </div>
        </div>

        {/* Spacer for fixed header */}
        <div className="h-14"></div>
      </>
    )
  }

  // Desktop sidebar
  return (
    <div className="fixed left-0 h-screen w-64 bg-white shadow-xl border-r border-gray-200 z-30">
      <SidebarContent />
    </div>
  )
}

export default Sidebar
